import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Alert,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import colors from "../../constants/colors";
import axiosInstance from "../../api/axiosInstance";

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });

    // Clear error message when user types
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    // Check if passwords match
    if (formData.password !== formData.confirmPassword) {
      setError("As palavras-passe não coincidem.");
      setValidated(true);
      return;
    }

    try {
      setLoading(true);
      // Map name to username as per API spec
      const requestData = {
        username: formData.name,
        email: formData.email,
        password: formData.password,
      };

      await axiosInstance.post("/auth/register", requestData);

      // Success scenario
      setSuccess(true);
      setFormData({ name: "", email: "", password: "", confirmPassword: "" });
      setValidated(false);

      // Redirect to login after short delay
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      console.error("Registration error:", error);

      // Handle different error responses
      if (error.response) {
        // The request was made and the server responded with an error status
        setError(
          error.response.data?.message ||
            "Ocorreu um erro ao processar o registo."
        );
      } else if (error.request) {
        // The request was made but no response was received
        setError(
          "Não foi possível conectar ao servidor. Por favor, tente novamente mais tarde."
        );
      } else {
        // Something happened in setting up the request
        setError("Ocorreu um erro ao processar o teu registo.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-5" style={{backgroundColor: colors.background}}>
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow-sm" style={{borderColor: colors.secondary}}>
            <Card.Body className="p-4">
              <h2 className="text-center mb-4">Criar Conta</h2>

              {error && (
                <Alert
                  style={{
                    backgroundColor: colors.danger,
                    color: colors.light,
                  }}
                >
                  {error}
                </Alert>
              )}
              {success && (
                <Alert
                  style={{
                    backgroundColor: colors.success,
                    color: colors.light,
                  }}
                >
                  Registo efetuado com sucesso!<br />
                  Por favor, verifica o teu email para ativar a conta antes de iniciar sessão.
                </Alert>
              )}

              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="userName">
                  <Form.Label>Nome Completo</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="O teu nome completo"
                    style={{ borderColor: colors.secondary }}
                  />
                  <Form.Control.Feedback type="invalid">
                    Por favor insere o teu nome.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="userEmail">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="O teu email"
                    style={{ borderColor: colors.secondary }}
                  />
                  <Form.Control.Feedback type="invalid">
                    Por favor insira um email válido.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="userPassword">
                  <Form.Label>Palavra-passe</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength="6"
                    placeholder="A tua palavra-passe"
                    style={{ borderColor: colors.secondary }}
                  />
                  <Form.Control.Feedback type="invalid">
                    A palavra-passe deve ter pelo menos 6 caracteres.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="userConfirmPassword">
                  <Form.Label>Confirmar Palavra-passe</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    placeholder="Confirma a tua palavra-passe"
                    style={{ borderColor: colors.secondary }}
                  />
                  <Form.Control.Feedback type="invalid">
                    Por favor confirma a tua palavra-passe.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="termsAgreement">
                  <Form.Check
                    type="checkbox"
                    label={
                      <>
                        Concordo com os{" "}
                        <Link
                          to="/termos-privacidade"
                          style={{
                            color: colors.secondary,
                            textDecoration: "underline",
                          }}
                        >
                          termos e condições
                        </Link>
                      </>
                    }
                    required
                    feedback="Deve concordar antes de se registar."
                    feedbackType="invalid"
                  />
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button
                    type="submit"
                    disabled={loading}
                    style={{
                      backgroundColor: colors.secondary,
                      borderColor: colors.primary,
                    }}
                  >
                    {loading ? "A processar..." : "Registar"}
                  </Button>
                </div>
              </Form>

              <div className="mt-3 text-center">
                <p className="mb-0">
                  Já tem uma conta?{" "}
                  <Link
                    to="/login"
                    style={{
                      color: colors.secondary,
                      textDecoration: "underline",
                    }}
                  >
                    Iniciar Sessão
                  </Link>
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default RegisterPage;
